import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import {
  toTeacherInactivationFilterParams,
  toTeacherInactivationFilterSearch,
  toTeacherInactivationFilterValues,
  toTeacherInactivationListParams,
} from '../utils/teacherInactivationFilters';

const route = getRouteApi('/_auth/settings/teacher-inactivations/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toTeacherInactivationFilterValues(toTeacherInactivationFilterSearch(values));

export function useTeacherInactivationFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toTeacherInactivationFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({
          ...toTeacherInactivationFilterSearch(values),
          search: previous.search,
        }),
        replace: true,
      });
    },
  });

  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({ search: (previous) => ({ ...previous, search: text }), replace: true });
    },
  });

  const listParams = useMemo(() => toTeacherInactivationListParams(search), [search]);
  const summaryParams = useMemo(() => toTeacherInactivationFilterParams(search), [search]);

  return {
    ...filters,
    listParams,
    summaryParams,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    searchText,
    setSearchText,
  };
}
